import { Geolocation } from '@capacitor/geolocation';
import { createEmptyAddress, normalizeAddress } from './address';
import { APP } from '../config/app';
import { hasGoogleMapsKey } from '../config/maps';
import { isNativeApp } from './platform';

const mapCapacitorError = (error) => {
  const msg = (error?.message || '').toLowerCase();
  if (msg.includes('denied') || msg.includes('permission')) {
    return Object.assign(new Error('Location permission denied'), { code: 1 });
  }
  if (msg.includes('timeout') || msg.includes('timed out')) {
    return Object.assign(new Error('Location timeout'), { code: 3 });
  }
  return error;
};

const getNativePosition = async (options = {}) => {
  let perm = await Geolocation.checkPermissions();
  if (perm.location === 'prompt' || perm.location === 'prompt-with-rationale') {
    perm = await Geolocation.requestPermissions();
  }
  if (perm.location === 'denied') {
    throw Object.assign(new Error('Location permission denied'), { code: 1 });
  }
  try {
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: options.maximumAge ?? 120000,
    });
    return {
      coords: {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      },
    };
  } catch (e) {
    throw mapCapacitorError(e);
  }
};

const getBrowserPosition = (options = {}) => new Promise((resolve, reject) => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    reject(Object.assign(new Error('Location not supported'), { code: 'GEO_UNSUPPORTED' }));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    resolve,
    reject,
    {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: options.maximumAge ?? 120000,
    },
  );
});

/** Get device GPS position (native plugin in app shell, browser API on web). */
export const getCurrentPosition = async (options = {}) => {
  if (isNativeApp()) {
    try {
      return await getNativePosition(options);
    } catch (nativeErr) {
      // Fallback if plugin missing in old APK builds
      if (nativeErr?.code !== 'GEO_UNSUPPORTED') {
        try {
          return await getBrowserPosition(options);
        } catch {
          throw nativeErr;
        }
      }
      throw nativeErr;
    }
  }
  return getBrowserPosition(options);
};

export const waitForGoogleMaps = (maxMs = 8000) => new Promise((resolve, reject) => {
  if (typeof window === 'undefined') {
    reject(new Error('Google Maps unavailable'));
    return;
  }
  const ready = () => window.google?.maps?.Geocoder;
  if (ready()) {
    resolve();
    return;
  }
  const started = Date.now();
  const timer = setInterval(() => {
    if (ready()) {
      clearInterval(timer);
      resolve();
    } else if (Date.now() - started > maxMs) {
      clearInterval(timer);
      reject(new Error('Google Maps not ready'));
    }
  }, 120);
});

/** State names, pincodes, and other admin text that must not fill street/building. */
export const isGeocodeJunk = (value) => {
  const t = (value || '').trim();
  if (!t) return true;
  if (/^\d{6}$/.test(t)) return true;
  if (/^india$/i.test(t)) return true;
  if (/^asia$/i.test(t)) return true;
  if (/^andhra pradesh(\s+\d{6})?$/i.test(t)) return true;
  if (/andhra pradesh\s*\d{6}/i.test(t)) return true;
  if (/^(andhra pradesh|telangana|karnataka|tamil nadu)\s*$/i.test(t)) return true;
  if (/^piler(u)?(\s*,?\s*andhra pradesh)?$/i.test(t) && t.length < 30) return false;
  return false;
};

/** True when text is only town/state/pincode — never use as building. */
export const isAdminOnlyText = (value) => {
  const t = (value || '').trim();
  if (!t) return true;
  if (isGeocodeJunk(t)) return true;
  if (/^pileru?\s*,?\s*andhra pradesh\s*,?\s*\d{6}?$/i.test(t)) return true;
  if (/^piler\s*,?\s*andhra pradesh$/i.test(t)) return true;
  if (/^(pileru?|bodumalluvaripalle)\s*,?\s*andhra pradesh(\s*,?\s*\d{6})?$/i.test(t)) return true;
  return false;
};

export const looksLikeHouseNo = (value) => {
  const t = (value || '').trim();
  if (!t || t.length > 20) return false;
  if (/^(h\.?\s*no\.?|plot|flat|door|d\.?\s*no\.?)\s*/i.test(t)) return true;
  if (/^\d+[\s\-/]\d+/.test(t)) return true;
  if (/^\d+[a-z]?$/i.test(t)) return true;
  if (/^\d+[\-/]\d+[a-z]?$/i.test(t)) return true;
  return false;
};

const getAddressComponent = (components, ...types) => {
  for (const type of types) {
    const match = components.find((c) => c.types.includes(type));
    if (match?.long_name) return match.long_name;
  }
  return '';
};

const parseGoogleGeocodeResult = (result) => {
  const components = result.address_components || [];
  const streetNumber = getAddressComponent(components, 'street_number');
  const subpremise = getAddressComponent(components, 'subpremise');
  const premise = getAddressComponent(components, 'premise', 'establishment', 'point_of_interest');
  const route = getAddressComponent(components, 'route');
  const sublocality = getAddressComponent(components, 'sublocality_level_1', 'sublocality', 'neighborhood');
  const locality = getAddressComponent(components, 'locality', 'administrative_area_level_2');
  const state = getAddressComponent(components, 'administrative_area_level_1');
  const pincode = getAddressComponent(components, 'postal_code');

  let houseNo = [streetNumber, subpremise].filter(Boolean).join('-').trim();
  let building = [premise, route].filter(Boolean).filter((p) => p !== houseNo).join(', ').trim();

  if (!building && result.formatted_address) {
    const parts = result.formatted_address.split(',').map((p) => p.trim());
    for (const part of parts.slice(0, 4)) {
      if (isAdminOnlyText(part) || isGeocodeJunk(part)) continue;
      if (looksLikeHouseNo(part)) {
        if (!houseNo) houseNo = part;
      } else if (!building && part.length > 2 && part.toLowerCase() !== (locality || '').toLowerCase()) {
        building = part;
        break;
      }
    }
  }

  building = building.replace(/\s+asia\s*$/i, '').trim();
  if (isAdminOnlyText(building)) building = '';
  const area = sublocality || locality || 'Piler';
  if (building && building.toLowerCase() === area.toLowerCase()) {
    building = route || '';
  }

  return {
    area,
    city: locality || area || 'Piler',
    state: state || 'Andhra Pradesh',
    pincode: pincode?.replace(/\D/g, '').slice(0, 6) || undefined,
    houseNo,
    building: isAdminOnlyText(building) ? '' : building,
  };
};

/** Google Geocoder — best results in India when Maps JS is loaded. */
export const reverseGeocodeGoogle = async (lat, lng) => {
  await waitForGoogleMaps();
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== 'OK' || !results?.length) {
        reject(new Error(`Google geocode failed: ${status}`));
        return;
      }
      resolve(parseGoogleGeocodeResult(results[0]));
    });
  });
};

/** OpenStreetMap Nominatim — good fallback for rural/town areas. */
export const reverseGeocodeOsm = async (lat, lng) => {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('OSM geocode failed');
  const data = await res.json();
  const a = data.address || {};
  const area = a.suburb || a.neighbourhood || a.village || a.town || a.city || 'Piler';
  const houseNo = (a.house_number || '').trim();
  const building = [a.building, a.residential, a.road]
    .filter(Boolean)
    .filter((p) => p !== houseNo)
    .join(', ')
    .replace(/\s+asia\s*$/i, '')
    .trim();
  const buildingRaw = building && building.toLowerCase() !== area.toLowerCase() ? building : (a.road || '');
  return {
    area,
    city: a.city || a.town || a.village || area || 'Piler',
    state: a.state || 'Andhra Pradesh',
    pincode: (a.postcode || '').replace(/\D/g, '').slice(0, 6) || undefined,
    houseNo,
    building: isAdminOnlyText(buildingRaw) ? '' : buildingRaw,
  };
};

/** Reverse geocode via BigDataCloud (no API key). */
export const reverseGeocodeBigDataCloud = async (lat, lng) => {
  const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Could not detect address from location');
  const data = await res.json();
  const locality = (data.locality || data.city || '').trim();
  const street = (data.street || '').trim();
  const informative = data.localityInfo?.informative || [];
  const roadLike = informative.find((x) => /road|street|route|highway/i.test(x.description || ''));
  let building = street || (roadLike?.name || '').trim();
  building = building.replace(/\s+asia\s*$/i, '').trim();
  if (building && locality && building.toLowerCase() === locality.toLowerCase()) {
    building = '';
  }
  if (building && looksLikeHouseNo(building)) {
    return {
      area: locality || 'Piler',
      city: data.city || locality || 'Piler',
      state: data.principalSubdivision || 'Andhra Pradesh',
      pincode: (data.postcode || '').replace(/\D/g, '').slice(0, 6) || undefined,
      houseNo: building,
      building: '',
    };
  }
  return {
    area: locality || 'Piler',
    city: data.city || locality || 'Piler',
    state: data.principalSubdivision || 'Andhra Pradesh',
    pincode: (data.postcode || '').replace(/\D/g, '').slice(0, 6) || undefined,
    houseNo: '',
    building,
  };
};

const mergeGeocodeResults = (...results) => {
  const valid = results.filter(Boolean);
  const pick = (key) => valid.find((r) => r[key]?.trim())?.[key]?.trim() || '';

  let houseNo = pick('houseNo');
  let building = pick('building');

  if (!houseNo && building && looksLikeHouseNo(building)) {
    houseNo = building;
    building = valid.find((r) => r.building?.trim() && !looksLikeHouseNo(r.building))?.building?.trim()
      || valid.find((r) => r.route)?.route
      || '';
  }

  if (houseNo && building === houseNo) building = '';
  if (building && isAdminOnlyText(building)) building = '';

  return {
    area: pick('area') || 'Piler',
    city: pick('city') || 'Piler',
    state: pick('state') || 'Andhra Pradesh',
    pincode: pick('pincode') || undefined,
    houseNo,
    building,
  };
};

const fallbackGeo = (keepArea = '') => ({
  area: keepArea || 'Piler',
  city: 'Piler',
  state: 'Andhra Pradesh',
  pincode: APP.defaultPincode,
  houseNo: '',
  building: '',
});

/** Reverse geocode — Google first (if key), then OSM, then BigDataCloud. Never throws. */
export const reverseGeocode = async (lat, lng, keepArea = '') => {
  const attempts = [];

  if (hasGoogleMapsKey()) {
    try {
      attempts.push(await reverseGeocodeGoogle(lat, lng));
    } catch (e) {
      console.warn('Google reverse geocode:', e.message);
    }
  }

  try {
    attempts.push(await reverseGeocodeOsm(lat, lng));
  } catch (e) {
    console.warn('OSM reverse geocode:', e.message);
  }

  try {
    attempts.push(await reverseGeocodeBigDataCloud(lat, lng));
  } catch (e) {
    console.warn('BigDataCloud reverse geocode:', e.message);
  }

  if (!attempts.length) return fallbackGeo(keepArea);
  return mergeGeocodeResults(...attempts);
};

/** Fix swapped / misplaced geocode values before showing in form. */
export const cleanAutoGeocodeFields = (addr) => {
  if (!addr) return addr;
  let houseNo = (addr.houseNo || '').trim();
  let building = (addr.building || '').trim();
  let landmark = (addr.landmark || '').trim();

  if (/^near\s+/i.test(landmark)) landmark = '';
  building = building.replace(/\s+asia\s*$/i, '').trim();

  if (!houseNo && building && looksLikeHouseNo(building)) {
    houseNo = building;
    building = '';
  }
  if (houseNo && building === houseNo) building = '';
  if (building && isAdminOnlyText(building)) building = '';
  if (building && addr.area && building.toLowerCase() === addr.area.toLowerCase()) {
    building = '';
  }

  return { ...addr, houseNo, building, landmark };
};

export const LOCATION_SKIP_KEY = (uid) => `mytownly_location_skip_${uid}`;

export const hasSavedGeoAddress = (addresses = []) =>
  addresses.some((a) => Number.isFinite(a?.lat) && Number.isFinite(a?.lng));

/** Build a checkout-ready address from map pin or GPS coordinates. */
export const buildAddressFromCoords = async ({
  lat,
  lng,
  profile,
  phone,
  keepHouseNo = '',
  keepBuilding = '',
  keepLandmark = '',
  keepArea = '',
} = {}) => {
  const geo = await reverseGeocode(lat, lng, keepArea);
  const phone10 = (phone || '').replace(/\D/g, '').slice(-10);
  return cleanAutoGeocodeFields(normalizeAddress(createEmptyAddress({
    id: `pin_${Date.now()}`,
    type: 'other',
    label: 'Delivery pin',
    emoji: '📍',
    lat,
    lng,
    area: keepArea || geo.area,
    city: geo.city,
    state: geo.state,
    pincode: geo.pincode || APP.defaultPincode,
    building: keepBuilding || geo.building || '',
    landmark: keepLandmark || '',
    houseNo: keepHouseNo || geo.houseNo || '',
    receiverName: profile?.name || '',
    receiverPhone: phone10,
    isDefault: false,
  })));
};

/** Build a checkout-ready address from fresh GPS (Blinkit/Zepto-style). */
export const buildAddressFromGps = async ({
  profile,
  phone,
  keepHouseNo = '',
  keepBuilding = '',
  keepLandmark = '',
  keepArea = '',
} = {}) => {
  const pos = await getCurrentPosition({ maximumAge: 0 });
  const { latitude: lat, longitude: lng } = pos.coords;
  return buildAddressFromCoords({
    lat,
    lng,
    profile,
    phone,
    keepHouseNo,
    keepBuilding,
    keepLandmark,
    keepArea,
  });
};
